import * as Sentry from '@sentry/react';

import { getByPath } from '@/utils';
import { executeStringFn } from '@/utils/executeFn';
import { safeObject } from '@/utils/object';

type Properties<TRjsfProps extends Record<string, unknown>> = {
  props?: Partial<TRjsfProps>;
  propsPath?: Partial<TRjsfProps>;
  propsFn?: Partial<TRjsfProps>;
};

type SchemaWithConditionalProps<TRjsfProps extends Record<string, unknown>> =
  Properties<TRjsfProps> & {
    conditionalProps: ({
      /**
       * `if` is a JMESPath expression that evaluates to a boolean
       * */
      if?: string;
      /**
       * `ifFn` is a stringified function that return a boolean indicating whether the props should be applied
       * */
      ifFn?: string;
    } & Properties<TRjsfProps>)[];
  };

export const useDynamicProps = <TRjsfProps extends Record<string, unknown>>(
  rjsfProps: TRjsfProps
): TRjsfProps => {
  const context = rjsfProps.formContext;
  const schema = rjsfProps.schema as SchemaWithConditionalProps<TRjsfProps>;

  const propsStaticStore = safeObject(schema.props);
  const propsPathStore = safeObject(schema.propsPath);
  const propsFnStore = safeObject(schema.propsFn);

  if (hasConditions(rjsfProps)) {
    for (const cond of schema.conditionalProps) {
      if (!evaluateCondition(cond.if, cond.ifFn, context)) continue;

      Object.assign(propsStaticStore, cond.props);
      Object.assign(propsPathStore, cond.propsPath);
      Object.assign(propsFnStore, cond.propsFn);
    }
  }

  const resolvedPathProps = resolvePropsPath(propsPathStore, context);
  const resolvedFnProps = resolvePropsFn(propsFnStore, context);

  return {
    ...rjsfProps,
    ...propsStaticStore,
    ...resolvedPathProps,
    ...resolvedFnProps
  } as TRjsfProps;
};

const hasConditions = <TProps extends Record<string, unknown>>(
  rjsfProps: any
): rjsfProps is Omit<TProps, 'schema'> & {
  schema: SchemaWithConditionalProps<TProps>;
} => rjsfProps?.schema?.conditionalProps?.length > 0;

const resolvePropsPath = <TRjsfProps extends Record<string, unknown>>(
  propsPathStore: Partial<TRjsfProps>,
  formContext: unknown
): Partial<TRjsfProps> => {
  if (Object.keys(propsPathStore).length === 0) return {};
  const resolvedProps: Record<string, any> = {};

  Object.entries<string>(propsPathStore as any).forEach(([propName, path]) => {
    try {
      const resolvedValue = getByPath(formContext, path);
      if (resolvedValue !== undefined) resolvedProps[propName] = resolvedValue;
    } catch (error) {
      console.error('Error resolving props path:', propName, path, error);
    }
  });

  return resolvedProps as Partial<TRjsfProps>;
};

const resolvePropsFn = <TRjsfProps extends Record<string, unknown>>(
  propsFnStore: TRjsfProps,
  formContext: unknown
): TRjsfProps => {
  if (Object.keys(propsFnStore).length === 0) return {} as TRjsfProps;
  const resolvedProps: Record<string, any> = {};

  Object.entries(propsFnStore).forEach(([propName, fnString]) => {
    if (typeof fnString !== 'string') {
      console.error(
        `Expected function string for prop ${propName}, got:`,
        fnString
      );
      Sentry.captureMessage(
        `Expected function string for prop ${propName}, got: ${JSON.stringify(fnString, null, 2)}`
      );
      return;
    }
    const resolvedValue = executeFn(fnString, formContext);

    if (resolvedValue !== undefined && !(resolvedValue instanceof Error)) {
      resolvedProps[propName] = resolvedValue;
    }
  });
  return resolvedProps as TRjsfProps;
};

const executeFn = (fn: string, context: unknown): any | undefined | Error => {
  try {
    return executeStringFn(context, fn);
  } catch (error) {
    console.error('Error executing function:', fn, error);
    Sentry.captureException(error, {
      extra: { fn, context }
    });
    return new Error(`Error executing function: ${fn}`);
  }
};

const evaluateCondition = (
  path?: string,
  ifFn?: string,
  context?: any
): boolean => {
  if (path) {
    return !!getByPath(context, path);
  }
  if (ifFn) {
    try {
      return !!executeFn(ifFn, context);
    } catch {
      return false;
    }
  }
  return true;
};
